import React, { useEffect, useState } from 'react';
import { Table, Tag, Select, Button, Space, Image, message, Popconfirm, Input } from 'antd';
import request from '../utils/request';
import { fmtTime } from '../utils/format';

const statusMap: Record<string, { color: string; text: string }> = {
  PENDING: { color: 'orange', text: '待兑换' },
  COMPLETED: { color: 'green', text: '已完成' },
  EXPIRED: { color: 'red', text: '已过期' },
};

const PointsRedemptions: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string | undefined>();
  const [pageSize, setPageSize] = useState(10);
  const [kwInput, setKwInput] = useState('');
  const [keyword, setKeyword] = useState('');

  const fetchList = async (p = page) => {
    setLoading(true);
    try {
      const res: any = await request.get('/points/redemptions', {
        params: { page: p, pageSize, status, keyword: keyword || undefined },
      });
      setList(res.list);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, [page, status, keyword, pageSize]);

  const resetFilters = () => {
    setKwInput(''); setKeyword(''); setStatus(undefined); setPage(1);
  };

  // 确认兑换完成：线上=已发货，线下=已到店核销
  const handleComplete = async (id: number) => {
    await request.put(`/points/redemptions/${id}/complete`);
    message.success('已确认完成');
    fetchList();
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '用户', width: 150,
      render: (_: any, r: any) => r.user?.realName || r.user?.nickname || `用户#${r.userId}`,
    },
    { title: '手机号', width: 120, render: (_: any, r: any) => r.user?.phone || '-' },
    {
      title: '商品', dataIndex: 'productName', width: 180,
      render: (v: string, r: any) => (
        <Space>
          {r.productImage && <Image src={r.productImage} width={32} height={32} style={{ objectFit: 'cover' }} />}
          <span>{v}</span>
        </Space>
      ),
    },
    { title: '消耗积分', dataIndex: 'pointsCost', width: 90 },
    {
      title: '兑换路径', dataIndex: 'redeemType', width: 110,
      render: (v: string) => (
        <Tag color={v === 'OFFLINE' ? 'cyan' : 'geekblue'}>{v === 'OFFLINE' ? '线下到店' : '线上发货'}</Tag>
      ),
    },
    {
      title: '收货/核销信息', width: 220,
      render: (_: any, r: any) => r.redeemType === 'OFFLINE'
        ? (r.redeemCode ? <Tag color="gold" style={{ fontSize: 13 }}>核销码 {r.redeemCode}</Tag> : '-')
        : `${r.receiverName || ''} ${r.receiverPhone || ''} ${r.receiverAddress || ''}`,
    },
    {
      title: '状态', dataIndex: 'status', width: 90,
      render: (v: string) => <Tag color={statusMap[v]?.color}>{statusMap[v]?.text}</Tag>,
    },
    {
      title: '过期时间', dataIndex: 'expireAt', width: 150,
      render: (v: string) => v ? fmtTime(v) : '永不过期',
    },
    {
      title: '兑换时间', dataIndex: 'createdAt', width: 150,
      render: (v: string) => fmtTime(v),
    },
    {
      title: '操作', width: 110, fixed: 'right' as const,
      render: (_: any, record: any) => record.status === 'PENDING' ? (
        <Popconfirm
          title={record.redeemType === 'OFFLINE' ? '确认已到店核销？' : '确认已发货完成？'}
          onConfirm={() => handleComplete(record.id)}
        >
          <Button size="small" type="primary">确认完成</Button>
        </Popconfirm>
      ) : null,
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>积分兑换管理</h2>
        <Space>
          <Input.Search
            allowClear
            placeholder="核销码/商品名/收货人/手机号"
            style={{ width: 240 }}
            value={kwInput}
            onChange={(e) => setKwInput(e.target.value)}
            onSearch={(v) => { setPage(1); setKeyword(v.trim()); }}
          />
          <Select
            allowClear
            placeholder="状态筛选"
            style={{ width: 140 }}
            value={status}
            onChange={(v) => { setStatus(v); setPage(1); }}
            options={[
              { label: '待兑换', value: 'PENDING' },
              { label: '已完成', value: 'COMPLETED' },
              { label: '已过期', value: 'EXPIRED' },
            ]}
          />
          {(keyword || status) && <Button onClick={resetFilters}>重置</Button>}
        </Space>
      </div>
      <Table columns={columns} dataSource={list} rowKey="id" loading={loading} scroll={{ x: 1400 }}
        pagination={{
          current: page, total, pageSize, showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
        }} />
    </div>
  );
};

export default PointsRedemptions;
